import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/libs/axiosClient";
import { queryKeys } from "@/shared/libs/queryClient";
import { toast } from "@/shared/hooks/useToast";
import type {
  UniversalThrottlingPolicy,
  UniversalThrottlingMethod,
} from "@/types/preferences_types";

// ============================================
// API 응답/요청 타입
// ============================================

interface CreatePolicyRequest {
  channel: string;
  throttlingKey: string;
  throttlingMethod: UniversalThrottlingMethod[];
}

interface UpdatePolicyRequest {
  channel: string;
  throttlingKey: string;
  throttlingMethod: UniversalThrottlingMethod[];
}

// ============================================
// API 함수들
// ============================================

/**
 * 모든 스로틀링 정책 조회
 */
async function fetchUniversalThrottlingPolicies(): Promise<UniversalThrottlingPolicy[]> {
  return api.get<UniversalThrottlingPolicy[]>("/universal-throttling-policies/");
}

/**
 * 특정 정책 조회
 */
async function fetchUniversalThrottlingPolicyById(policyId: string): Promise<UniversalThrottlingPolicy> {
  return api.get<UniversalThrottlingPolicy>(`/universal-throttling-policies/${policyId}`);
}

/**
 * 사용 가능한 채널 목록 조회
 */
async function fetchChannels(): Promise<string[]> {
  const response = await api.get<{ channels: string[] }>("/universal-throttling-policies/channels");
  return response.channels;
}

/**
 * 정책 생성
 */
async function createPolicy(request: CreatePolicyRequest): Promise<UniversalThrottlingPolicy> {
  return api.post<UniversalThrottlingPolicy>("/universal-throttling-policies/", request);
}

/**
 * 정책 수정
 */
async function updatePolicy(
  policyId: string,
  request: UpdatePolicyRequest
): Promise<UniversalThrottlingPolicy> {
  return api.put<UniversalThrottlingPolicy>(`/universal-throttling-policies/${policyId}`, request);
}

/**
 * 정책 삭제 (여러 개)
 */
async function deletePolicies(policyIds: string[]): Promise<void> {
  return api.delete<void>("/universal-throttling-policies/", {
    data: policyIds,
  });
}

// ============================================
// Query 훅들
// ============================================

/**
 * 모든 스로틀링 정책 조회 Query
 */
export function useUniversalThrottlingPoliciesQuery() {
  return useQuery({
    queryKey: queryKeys.universalThrottlingPolicies.list(),
    queryFn: fetchUniversalThrottlingPolicies,
  });
}

/**
 * 특정 정책 조회 Query
 */
export function useUniversalThrottlingPolicyDetailQuery(policyId: string) {
  return useQuery({
    queryKey: queryKeys.universalThrottlingPolicies.detail(policyId),
    queryFn: () => fetchUniversalThrottlingPolicyById(policyId),
    enabled: !!policyId,
  });
}

/**
 * 채널 목록 조회 Query
 */
export function useChannelsQuery() {
  return useQuery({
    queryKey: queryKeys.universalThrottlingPolicies.channels(),
    queryFn: fetchChannels,
    staleTime: Infinity,
  });
}

// ============================================
// Mutation 훅들
// ============================================

/**
 * 정책 생성 Mutation
 */
export function useCreatePolicyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPolicy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.universalThrottlingPolicies.list() });
      toast({
        title: "생성 완료",
        description: "스로틀링 정책이 생성되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "생성 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * 정책 수정 Mutation
 */
export function useUpdatePolicyMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ policyId, request }: { policyId: string; request: UpdatePolicyRequest }) =>
      updatePolicy(policyId, request),
    onSuccess: (_, { policyId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.universalThrottlingPolicies.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.universalThrottlingPolicies.detail(policyId) });
      toast({
        title: "수정 완료",
        description: "스로틀링 정책이 수정되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "수정 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/**
 * 정책 삭제 Mutation
 */
export function useDeletePoliciesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePolicies,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.universalThrottlingPolicies.list() });
      toast({
        title: "삭제 완료",
        description: "스로틀링 정책이 삭제되었습니다.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "삭제 실패",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

// Export request types for use in components
export type { CreatePolicyRequest, UpdatePolicyRequest };
